import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Tooltip,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ContentCopy as DuplicateIcon,
  WarningAmberRounded as WarnIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { SuiviPhase, SuiviPhaseCreate, SuiviPhaseStep } from '../../../types/suivi';
import { suiviAPI, ateliersAPI } from '../../../services/api';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import PermissionButton from '../../../components/PermissionButton';
import { extractErrorMessage } from '../../../utils/errorHandler';

interface AtelierDropdown {
  id: number;
  code: string;
  name: string;
}

const CONFIG_COLOR = '#1976D2';

// --------- Sortable Step row ---------
interface SortableStepProps {
  step: SuiviPhaseStep;
  onEdit: () => void;
  onDelete: () => void;
}

const SortableStep: React.FC<SortableStepProps> = ({ step, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `step-${step.id}`,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1,
        borderRadius: 1,
        bgcolor: isDragging ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
        opacity: step.is_active ? 1 : 0.55,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab', p: 0.25 }}>
        <DragIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      </IconButton>
      <Chip label={step.code} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }} />
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
        {step.name}
      </Typography>
      {step.description && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {step.description}
        </Typography>
      )}
      {!step.is_active && <Chip label="Inactif" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
      <IconButton size="small" onClick={onEdit} color="primary" sx={{ p: 0.5 }}>
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onDelete} color="error" sx={{ p: 0.5 }}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// --------- Sortable Phase accordion ---------
interface SortablePhaseProps {
  phase: SuiviPhase;
  steps: SuiviPhaseStep[];
  ateliers: AtelierDropdown[];
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  onEditPhase: () => void;
  onDeletePhase: () => void;
  onDuplicatePhase: () => void;
  onAddStep: () => void;
  onEditStep: (step: SuiviPhaseStep) => void;
  onDeleteStep: (step: SuiviPhaseStep) => void;
  onStepsReorder: (phaseId: number, fromIdx: number, toIdx: number) => void;
}

const SortablePhase: React.FC<SortablePhaseProps> = ({
  phase, steps, ateliers, expanded, onExpandChange,
  onEditPhase, onDeletePhase, onDuplicatePhase,
  onAddStep, onEditStep, onDeleteStep, onStepsReorder,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `phase-${phase.id}`,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const atelierName = phase.atelier_name || ateliers.find(a => a.id === phase.atelier_id)?.name;

  // Soft mismatch warning: phase has an atelier, but neither name contains the other (case-insensitive).
  const showMismatch = useMemo(() => {
    if (!phase.atelier_id || !atelierName) return false;
    const p = phase.name.toLowerCase();
    const a = atelierName.toLowerCase();
    return !p.includes(a) && !a.includes(p);
  }, [phase.name, phase.atelier_id, atelierName]);

  const handleStepDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = steps.findIndex((s) => `step-${s.id}` === active.id);
    const toIdx = steps.findIndex((s) => `step-${s.id}` === over.id);
    if (fromIdx === -1 || toIdx === -1) return;
    onStepsReorder(phase.id, fromIdx, toIdx);
  };

  return (
    <Accordion
      ref={setNodeRef}
      expanded={expanded}
      onChange={(_, isExp) => onExpandChange(isExp)}
      disableGutters
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: isDragging ? 6 : 1,
        opacity: isDragging ? 0.85 : 1,
        '&:before': { display: 'none' },
        bgcolor: phase.is_active ? 'background.paper' : 'action.disabledBackground',
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon />}
        sx={{
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
        }}
      >
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          sx={{ cursor: 'grab', p: 0.5 }}
        >
          <DragIcon sx={{ color: 'text.secondary' }} />
        </IconButton>

        <Chip
          label={phase.code}
          size="small"
          sx={{ fontFamily: 'monospace', fontWeight: 600, bgcolor: CONFIG_COLOR, color: 'white' }}
        />

        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {phase.name}
        </Typography>

        {atelierName && (
          <Chip
            label={atelierName}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}

        {showMismatch && (
          <Tooltip title={`Le nom de la phase "${phase.name}" ne correspond pas à l'atelier "${atelierName}". Vérifiez la configuration.`}>
            <WarnIcon sx={{ color: 'warning.main', fontSize: 20 }} />
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        <Chip
          label={`${steps.length} étape${steps.length > 1 ? 's' : ''}`}
          size="small"
          sx={{ height: 22, fontSize: '0.7rem' }}
        />

        {!phase.is_active && (
          <Chip label="Inactif" size="small" color="default" sx={{ height: 22, fontSize: '0.7rem' }} />
        )}

        <Tooltip title="Dupliquer la phase">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDuplicatePhase(); }}>
            <DuplicateIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Modifier la phase">
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onEditPhase(); }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Supprimer la phase">
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDeletePhase(); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 1 }} />
        {phase.description && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontStyle: 'italic' }}>
            {phase.description}
          </Typography>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStepDragEnd}>
          <SortableContext items={steps.map((s) => `step-${s.id}`)} strategy={verticalListSortingStrategy}>
            <Stack spacing={0.25} sx={{ mb: 1 }}>
              {steps.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', py: 1, textAlign: 'center' }}>
                  Aucune étape pour cette phase
                </Typography>
              ) : (
                steps.map((step) => (
                  <SortableStep
                    key={step.id}
                    step={step}
                    onEdit={() => onEditStep(step)}
                    onDelete={() => onDeleteStep(step)}
                  />
                ))
              )}
            </Stack>
          </SortableContext>
        </DndContext>

        <Button startIcon={<AddIcon />} size="small" onClick={onAddStep} sx={{ mt: 0.5 }}>
          Ajouter une étape
        </Button>
      </AccordionDetails>
    </Accordion>
  );
};

// --------- Main page ---------
const SuiviWorkflowManagement: React.FC = () => {
  const [phases, setPhases] = useState<SuiviPhase[]>([]);
  const [steps, setSteps] = useState<SuiviPhaseStep[]>([]);
  const [ateliers, setAteliers] = useState<AtelierDropdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedPhaseId, setExpandedPhaseId] = useState<number | null>(null);

  // Phase dialog
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<SuiviPhase | null>(null);
  const [phaseForm, setPhaseForm] = useState<Partial<SuiviPhase>>({
    code: '', name: '', description: '', atelier_id: undefined, order: 0, is_active: true,
  });

  // Step dialog
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<SuiviPhaseStep | null>(null);
  const [stepForm, setStepForm] = useState<Partial<SuiviPhaseStep>>({
    phase_id: 0, code: '', name: '', description: '', order: 0, is_active: true,
  });

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [phasesRes, stepsRes, ateliersRes] = await Promise.all([
        suiviAPI.getPhases(0, 500),
        suiviAPI.getSteps(undefined, 0, 1000),
        ateliersAPI.getAllForDropdown(),
      ]);
      setPhases([...phasesRes.items].sort((a, b) => a.order - b.order));
      setSteps([...stepsRes.items].sort((a, b) => a.order - b.order));
      setAteliers(ateliersRes);
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const stepsByPhase = useMemo(() => {
    const map: Record<number, SuiviPhaseStep[]> = {};
    steps.forEach((s) => {
      if (!map[s.phase_id]) map[s.phase_id] = [];
      map[s.phase_id].push(s);
    });
    Object.keys(map).forEach((k) => map[Number(k)].sort((a, b) => a.order - b.order));
    return map;
  }, [steps]);

  // --- Phase handlers ---
  const openPhaseDialog = (phase?: SuiviPhase) => {
    if (phase) {
      setEditingPhase(phase);
      setPhaseForm({
        code: phase.code, name: phase.name, description: phase.description || '',
        atelier_id: phase.atelier_id, order: phase.order, is_active: phase.is_active,
      });
    } else {
      setEditingPhase(null);
      setPhaseForm({
        code: '', name: '', description: '', atelier_id: undefined,
        order: phases.length, is_active: true,
      });
    }
    setPhaseDialogOpen(true);
  };

  const savePhase = async () => {
    if (!phaseForm.code?.trim() || !phaseForm.name?.trim()) {
      showError('Le code et le nom sont obligatoires');
      return;
    }
    try {
      setLoading(true);
      if (editingPhase) {
        await suiviAPI.updatePhase(editingPhase.id, {
          code: phaseForm.code, name: phaseForm.name,
          description: phaseForm.description || undefined,
          atelier_id: phaseForm.atelier_id || undefined,
          order: phaseForm.order, is_active: phaseForm.is_active,
        });
        showSuccess('Phase modifiée');
      } else {
        await suiviAPI.createPhase({
          code: phaseForm.code!, name: phaseForm.name!,
          description: phaseForm.description || undefined,
          atelier_id: phaseForm.atelier_id || undefined,
          order: phaseForm.order, is_active: phaseForm.is_active,
        });
        showSuccess('Phase créée');
      }
      setPhaseDialogOpen(false);
      await fetchAll();
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const deletePhase = (phase: SuiviPhase) => {
    showConfirm(
      `Supprimer la phase "${phase.name}" et toutes ses étapes ?`,
      async () => {
        try {
          setLoading(true);
          await suiviAPI.deletePhase(phase.id);
          showSuccess('Phase supprimée');
          await fetchAll();
        } catch (err) {
          showError(extractErrorMessage(err));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const duplicatePhase = async (phase: SuiviPhase) => {
    try {
      setLoading(true);
      const created = await suiviAPI.createPhase({
        code: `${phase.code}-COPY`,
        name: `${phase.name} (copie)`,
        description: phase.description,
        atelier_id: phase.atelier_id,
        order: phases.length,
        is_active: phase.is_active,
      } as SuiviPhaseCreate);

      const sourceSteps = stepsByPhase[phase.id] || [];
      for (const s of sourceSteps) {
        await suiviAPI.createStep({
          phase_id: created.id,
          code: s.code, name: s.name, description: s.description,
          order: s.order, is_active: s.is_active,
        });
      }
      showSuccess(`Phase "${phase.name}" dupliquée avec ${sourceSteps.length} étape(s)`);
      await fetchAll();
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePhaseDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = phases.findIndex((p) => `phase-${p.id}` === active.id);
    const toIdx = phases.findIndex((p) => `phase-${p.id}` === over.id);
    if (fromIdx === -1 || toIdx === -1) return;

    const reordered = arrayMove(phases, fromIdx, toIdx);
    setPhases(reordered);

    try {
      // Persist new order for affected items only
      const start = Math.min(fromIdx, toIdx);
      const end = Math.max(fromIdx, toIdx);
      const updates = reordered.slice(start, end + 1).map((p, i) =>
        suiviAPI.updatePhase(p.id, { order: start + i })
      );
      await Promise.all(updates);
    } catch (err) {
      showError('Erreur lors du réordonnancement — rechargement.');
      await fetchAll();
    }
  };

  // --- Step handlers ---
  const openStepDialog = (phaseId: number, step?: SuiviPhaseStep) => {
    if (step) {
      setEditingStep(step);
      setStepForm({
        phase_id: step.phase_id, code: step.code, name: step.name,
        description: step.description || '', order: step.order, is_active: step.is_active,
      });
    } else {
      setEditingStep(null);
      const existing = stepsByPhase[phaseId] || [];
      setStepForm({
        phase_id: phaseId, code: '', name: '', description: '',
        order: existing.length, is_active: true,
      });
    }
    setStepDialogOpen(true);
  };

  const saveStep = async () => {
    if (!stepForm.code?.trim() || !stepForm.name?.trim() || !stepForm.phase_id) {
      showError('Le code, le nom et la phase sont obligatoires');
      return;
    }
    try {
      setLoading(true);
      if (editingStep) {
        await suiviAPI.updateStep(editingStep.id, {
          phase_id: stepForm.phase_id, code: stepForm.code, name: stepForm.name,
          description: stepForm.description || undefined,
          order: stepForm.order, is_active: stepForm.is_active,
        });
        showSuccess('Étape modifiée');
      } else {
        await suiviAPI.createStep({
          phase_id: stepForm.phase_id!, code: stepForm.code!, name: stepForm.name!,
          description: stepForm.description || undefined,
          order: stepForm.order, is_active: stepForm.is_active,
        });
        showSuccess('Étape créée');
      }
      setStepDialogOpen(false);
      await fetchAll();
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const deleteStep = (step: SuiviPhaseStep) => {
    showConfirm(
      `Supprimer l'étape "${step.name}" ?`,
      async () => {
        try {
          setLoading(true);
          await suiviAPI.deleteStep(step.id);
          showSuccess('Étape supprimée');
          await fetchAll();
        } catch (err) {
          showError(extractErrorMessage(err));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleStepsReorder = async (phaseId: number, fromIdx: number, toIdx: number) => {
    const phaseSteps = stepsByPhase[phaseId] || [];
    const reordered = arrayMove(phaseSteps, fromIdx, toIdx);

    // Update local steps state
    setSteps((prev) => {
      const others = prev.filter((s) => s.phase_id !== phaseId);
      const renumbered = reordered.map((s, i) => ({ ...s, order: i }));
      return [...others, ...renumbered];
    });

    try {
      const start = Math.min(fromIdx, toIdx);
      const end = Math.max(fromIdx, toIdx);
      const updates = reordered.slice(start, end + 1).map((s, i) =>
        suiviAPI.updateStep(s.id, { order: start + i })
      );
      await Promise.all(updates);
    } catch (err) {
      showError('Erreur lors du réordonnancement — rechargement.');
      await fetchAll();
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Phases & Étapes</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Gérez la hiérarchie complète du suivi. Glissez pour réorganiser.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Recharger">
            <IconButton onClick={fetchAll} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Phases Suivi"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openPhaseDialog()}
          >
            Nouvelle Phase
          </PermissionButton>
        </Box>
      </Box>

      {loading && phases.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : phases.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Aucune phase configurée. Cliquez sur "Nouvelle Phase" pour commencer.
          </Typography>
        </Paper>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePhaseDragEnd}>
          <SortableContext items={phases.map((p) => `phase-${p.id}`)} strategy={verticalListSortingStrategy}>
            {phases.map((phase) => (
              <SortablePhase
                key={phase.id}
                phase={phase}
                steps={stepsByPhase[phase.id] || []}
                ateliers={ateliers}
                expanded={expandedPhaseId === phase.id}
                onExpandChange={(isExp) => setExpandedPhaseId(isExp ? phase.id : null)}
                onEditPhase={() => openPhaseDialog(phase)}
                onDeletePhase={() => deletePhase(phase)}
                onDuplicatePhase={() => duplicatePhase(phase)}
                onAddStep={() => openStepDialog(phase.id)}
                onEditStep={(s) => openStepDialog(phase.id, s)}
                onDeleteStep={(s) => deleteStep(s)}
                onStepsReorder={handleStepsReorder}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {/* Phase dialog */}
      <Dialog open={phaseDialogOpen} onClose={() => setPhaseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPhase ? 'Modifier la Phase' : 'Nouvelle Phase'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                variant="filled" fullWidth label="Code" required
                value={phaseForm.code || ''}
                onChange={(e) => setPhaseForm({ ...phaseForm, code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                variant="filled" fullWidth label="Nom" required
                value={phaseForm.name || ''}
                onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled" fullWidth label="Description" multiline rows={2}
                value={phaseForm.description || ''}
                onChange={(e) => setPhaseForm({ ...phaseForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="filled" fullWidth>
                <InputLabel>Atelier</InputLabel>
                <Select
                  value={phaseForm.atelier_id || ''}
                  onChange={(e) => setPhaseForm({ ...phaseForm, atelier_id: (e.target.value as number) || undefined })}
                >
                  <MenuItem value=""><em>Aucun</em></MenuItem>
                  {ateliers.map((a) => (
                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={phaseForm.is_active ?? true}
                    onChange={(e) => setPhaseForm({ ...phaseForm, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhaseDialogOpen(false)}>Annuler</Button>
          <Button onClick={savePhase} variant="contained" disabled={loading}>
            {editingPhase ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step dialog */}
      <Dialog open={stepDialogOpen} onClose={() => setStepDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingStep ? "Modifier l'Étape" : 'Nouvelle Étape'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl variant="filled" fullWidth required>
                <InputLabel>Phase</InputLabel>
                <Select
                  value={stepForm.phase_id || ''}
                  onChange={(e) => setStepForm({ ...stepForm, phase_id: e.target.value as number })}
                >
                  {phases.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.code} — {p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                variant="filled" fullWidth label="Code" required
                value={stepForm.code || ''}
                onChange={(e) => setStepForm({ ...stepForm, code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                variant="filled" fullWidth label="Nom" required
                value={stepForm.name || ''}
                onChange={(e) => setStepForm({ ...stepForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled" fullWidth label="Description" multiline rows={2}
                value={stepForm.description || ''}
                onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={stepForm.is_active ?? true}
                    onChange={(e) => setStepForm({ ...stepForm, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStepDialogOpen(false)}>Annuler</Button>
          <Button onClick={saveStep} variant="contained" disabled={loading}>
            {editingStep ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default SuiviWorkflowManagement;
